// src/components/PropertiesComparisonList.tsx
import React, { useMemo, useRef, useState } from 'react';
import { Collapse, Button } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { ComparisonResult } from '../types';
import ComparisonList from './comparison/ComparisonList';
import {colorBySimilarity} from "../services/utils/colorUtils";
import { useSimSettings } from '../hooks/useSimSettings';

const { Panel } = Collapse;

interface PropertiesComparisonListProps {
    /** Массив результатов сравнения, у которых propB ссылается на какое-то PropertyFull. */
    data: ComparisonResult[];

    /** Флаг, показывать ли кнопку «перейти к следующей группе». */
    showNextButton?: boolean;
}

interface GroupedData {
    typeName: string;
    items: ComparisonResult[];
}

/**
 * Группировка ComparisonResult по типу свойства (propB.type?.name).
 * Выводим Collapse.Panel для каждой группы,
 * в заголовке — название типа + средняя схожесть, покрашенная в цвет.
 */
const PropertiesComparisonList: React.FC<PropertiesComparisonListProps> = ({
                                                                               data,
                                                                               showNextButton = false,
                                                                           }) => {
    const { calcSim } = useSimSettings();
    const groupRefs = useRef<(HTMLDivElement | null)[]>([]);
    const [currentPanelIndex, setCurrentPanelIndex] = useState(0);

    // Группируем результаты по имени типа propB.type
    const groupedData: GroupedData[] = useMemo(() => {
        const map: Record<string, ComparisonResult[]> = {};

        data.forEach((item) => {
            const typeName = item.propB?.type?.name || 'Неопределённые';
            if (!map[typeName]) {
                map[typeName] = [];
            }
            map[typeName].push(item);
        });

        return Object.entries(map).map(([typeName, items]) => ({
            typeName,
            items,
        }));
    }, [data]);

    // Прокрутка к следующей «панели»
    const handleScrollToNextPanel = () => {
        const nextIndex = (currentPanelIndex + 1) % groupRefs.current.length;
        groupRefs.current[nextIndex]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setCurrentPanelIndex(nextIndex);
    };

    const getGroupAverage = (items: ComparisonResult[]) => {
        if (!items.length) return 0;
        let sum = 0;
        items.forEach((i) => { sum += calcSim(i.similarity); });
        return sum / items.length;
    };

    return (
        <div style={{ position: 'relative', paddingBottom: '60px' }}>
            {groupedData.map((group, index) => {
                const avg = getGroupAverage(group.items);
                const headerColor = colorBySimilarity(avg);

                /**
                 * Заголовок панели: «<Название типа> (avg XX%), кружок с цветом»
                 */
                const header = (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        {/* Текстовый блок: название + число процентов */}
                        <div style={{ marginRight: 8 }}>
                            {group.typeName} (сред.: {Math.round(avg * 100) / 100})
                        </div>

                        {/* «Бейдж» цвета (кружок) */}
                        <div
                            style={{
                                width: 12,
                                height: 12,
                                backgroundColor: headerColor,
                                borderRadius: '50%',
                            }}
                        />
                    </div>
                );

                return (
                    <div
                        key={group.typeName}
                        ref={(el) => (groupRefs.current[index] = el)}
                        style={{ marginBottom: '1rem' }}
                    >
                        <Collapse defaultActiveKey={['1']}>
                            <Panel header={header} key="1">
                                {/*
                  Внутри каждой группы выводим универсальный ComparisonList
                  (автоматически сортируемый, с нужными цветами и т.д.)
                */}
                                <ComparisonList data={group.items} />
                            </Panel>
                        </Collapse>
                    </div>
                );
            })}

            {showNextButton && groupedData.length > 1 && (
                <Button
                    onClick={handleScrollToNextPanel}
                    type="primary"
                    shape="circle"
                    icon={<DownOutlined />}
                    size="large"
                    style={{
                        position: 'sticky',
                        bottom: '10px',
                        right: '-10px',
                        zIndex: 10,
                    }}
                />
            )}
        </div>
    );
};

export default PropertiesComparisonList;
